import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api-client';
import { useAuthStore, type UsuarioSession } from '@/stores/auth.store';

interface PrivateRouteProps {
  children: ReactNode;
}

/**
 * Wrapper de rota privada — redireciona para `/login` se não houver
 * `usuario` no store. Mantém a `location` original para retornar após
 * login bem-sucedido.
 *
 * P1-Auth (ADR 0018): com tokens em cookies httpOnly, o frontend não
 * pode verificar a validade da sessão lendo localStorage. Ao montar, dispara
 * `GET /auth/me` para confirmar que o cookie ainda é válido:
 *
 *   - 200 → renderiza `children` e sincroniza o `usuario` no store
 *     (cobre o cenário "nome alterado no banco enquanto a sessão estava
 *     ociosa").
 *   - 401 → `logout()` + redireciona para `/login`.
 *   - Pending (1ª chamada) → exibe skeleton; evita flash de "redirect"
 *     enquanto o cookie ainda está sendo validado.
 *
 * Custo: 1 request extra por reload de uma rota privada — aceitável e
 * documentado no ADR 0018.
 */
export function PrivateRoute({ children }: PrivateRouteProps) {
  const usuario = useAuthStore((s) => s.usuario);
  const logout = useAuthStore((s) => s.logout);
  const setUsuario = useAuthStore((s) => s.setUsuario);
  const location = useLocation();

  // GET /auth/me — habilitado apenas se já há `usuario` no store. Sem
  // `usuario`, nem tentamos: vai direto para `/login`.
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const { data } = await api.get<UsuarioSession>('/auth/me');
      return data;
    },
    enabled: !!usuario,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  if (!usuario) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (meQuery.isError) {
    // Cookie expirado / inválido — backend retornou 401 e o interceptor
    // já tentou refresh (sem sucesso). Encerra sessão local e manda pro login.
    logout();
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (meQuery.isLoading) {
    return (
      <div className="space-y-3 p-6" data-testid="private-route-validando">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Sucesso: sincroniza dados frescos do usuário (nome/papeis podem ter
  // mudado). `setUsuario` faz merge — campos novos não-presentes ficam
  // como estavam.
  if (meQuery.data && meQuery.data.id === usuario.id) {
    if (
      meQuery.data.nome !== usuario.nome ||
      meQuery.data.email !== usuario.email ||
      meQuery.data.papeis.join(',') !== usuario.papeis.join(',')
    ) {
      setUsuario(meQuery.data);
    }
  }

  return <>{children}</>;
}
